import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Custom hook to fetch posts/message board data using optimized RPC
 * Includes server-side pagination, filtering, search debouncing, and post detail fetching
 *
 * @param {string} companyId - The company ID
 * @param {string} userId - The current user's ID
 * @param {object} options - Configuration options
 * @param {string[]} options.projectIds - Project IDs to filter by (for space/stack views)
 * @param {string} options.search - Search query
 * @param {boolean} options.isGlobalView - Whether this is the global posts view
 * @param {number} options.page - Current page
 * @param {number} options.limit - Items per page
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} { posts, summary, pagination, loading, error, refetch, fetchPostDetail }
 */
export const usePostsData = (companyId, userId, options = {}) => {
  const {
    projectIds = null,
    search = '',
    isGlobalView = false,
    page = 1,
    limit = 20,
    enabled = true
  } = options;

  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState({
    totalPosts: 0,
    pinnedPosts: 0,
    postsRequiringAction: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const debounceTimerRef = useRef(null);

  // Debounce search input (300ms)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!companyId || !enabled) {
      setLoading(false);
      return;
    }

    // For non-global views, we need project IDs
    if (!isGlobalView && (!projectIds || projectIds.length === 0)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc('get_posts_page_data', {
        p_company_id: companyId,
        p_user_id: userId || null,
        p_project_ids: isGlobalView ? null : projectIds,
        p_search: debouncedSearch || null,
        p_is_global_view: isGlobalView,
        p_page: page,
        p_limit: limit
      });

      if (fetchError) throw fetchError;

      setPosts(data.posts || []);
      setSummary(data.summary || {
        totalPosts: 0,
        pinnedPosts: 0,
        postsRequiringAction: 0
      });
      setPagination(data.pagination || {
        page: 1,
        limit: 20,
        totalCount: 0,
        totalPages: 0
      });
    } catch (err) {
      console.error('Error fetching posts data:', err);
      setError(err);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, projectIds, debouncedSearch, isGlobalView, page, limit, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Fetch detailed data for a specific post (comments, tracking, stats)
   * @param {string} postId - The post ID
   * @param {boolean} markAsRead - Whether to mark the post as read
   * @returns {object} { userTracking, trackingStats, allTracking, comments }
   */
  const fetchPostDetail = useCallback(async (postId, markAsRead = true) => {
    if (!postId || !userId) {
      return null;
    }

    try {
      const { data, error: fetchError } = await supabase.rpc('get_post_detail_data', {
        p_post_id: postId,
        p_user_id: userId,
        p_mark_as_read: markAsRead
      });

      if (fetchError) throw fetchError;

      if (data?.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (err) {
      console.error('Error fetching post detail:', err);
      toast.error('Failed to load post details');
      return null;
    }
  }, [userId]);

  /**
   * Optimistically update a post in the local state
   * @param {string} postId - The post ID
   * @param {object} updates - The fields to update
   */
  const updatePostLocally = useCallback((postId, updates) => {
    setPosts(prev => prev.map(post =>
      post.id === postId ? { ...post, ...updates } : post
    ));
  }, []);

  /**
   * Remove a post from the local state
   * @param {string} postId - The post ID to remove
   */
  const removePostLocally = useCallback((postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  }, []);

  /**
   * Add a post to the local state (at the beginning for new posts)
   * @param {object} post - The post to add
   */
  const addPostLocally = useCallback((post) => {
    setPosts(prev => {
      const updated = [post, ...prev];
      // Re-sort: pinned first, then by created_date
      return updated.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.created_date) - new Date(a.created_date);
      });
    });
  }, []);

  return {
    posts,
    summary,
    pagination,
    loading,
    error,
    refetch: fetchData,
    fetchPostDetail,
    updatePostLocally,
    removePostLocally,
    addPostLocally
  };
};
